import { Select as Base } from '@base-ui/react/select'
import { cva, type VariantProps } from 'class-variance-authority'
import { Check, ChevronDown } from 'lucide-react'
import type { ReactNode } from 'react'
import s from './Select.module.css'

/**
 * Select — composant de RÉFÉRENCE du design system (cf. ARCHITECTURE.md D20).
 *
 * Il illustre les trois mécanismes sur lesquels repose tout `packages/ui` :
 *
 *  1. **La primitive ne porte aucun style.** `@base-ui/react` fournit le rôle ARIA, le
 *     focus, la navigation clavier (flèches, Début/Fin, typeahead), le verrouillage du
 *     scroll et le positionnement anti-collision. L'apparence vient à 100 % du module CSS.
 *  2. **L'état est lu en CSS via les `data-*`**, jamais transformé en classe ici — donc
 *     aucune logique d'affichage dans le TSX.
 *  3. **Le DOM reste le nôtre.** La prop `render` d'une partie Base UI permet de
 *     substituer l'élément rendu au lieu d'en imbriquer un de plus. C'est ce qui évite le
 *     `<div>` parasite qui ferait diverger le rendu du prototype validé.
 *
 * ⚠️ Ce composant est **agnostique de la densité** : ses dimensions viennent de
 * `--control-*` / `--row-*`. Ne jamais en dériver une variante « tactile ».
 */

/**
 * La largeur du déclencheur est une **variante du composant**, pas une retouche depuis
 * l'écran : une classe d'écran qui redéfinirait `inline-size` a la même spécificité que
 * `.trigger` et ne gagne que par l'ordre d'émission des feuilles — un changement d'ordre
 * de bundle et le contrôle reprend 100 %. Ici, les deux règles vivent dans le même
 * fichier : l'ordre est déterminé une fois pour toutes.
 */
const trigger = cva(s.trigger, {
  variants: {
    width: {
      /** Remplit son conteneur — le cas d'un champ de formulaire. */
      full: null,
      /** Se dimensionne sur son libellé — le cas d'une barre de filtres en ligne. */
      auto: s.widthAuto,
    },
  },
  defaultVariants: { width: 'full' },
})

export interface SelectOption<T extends string> {
  value: T
  label: string
  disabled?: boolean
}

export interface SelectProps<T extends string> extends VariantProps<typeof trigger> {
  options: readonly SelectOption<T>[]
  value?: T | null
  defaultValue?: T | null
  /**
   * `null` est possible : la primitive émet une valeur vide quand la sélection est
   * effacée (champ non requis, réinitialisation de formulaire). Le type l'expose au lieu
   * de le masquer — c'est au code appelant de décider ce que vaut « aucune valeur ».
   */
  onValueChange?: (value: T | null) => void
  /** Affiché tant qu'aucune valeur n'est choisie — stylé par `[data-placeholder]`. */
  placeholder?: ReactNode
  /**
   * Le contrôle **restreint** ce que l'écran montre : le déclencheur s'allume, comme le
   * `.fbtn.active` du prototype. Sans lui, un agent qui a filtré sur une ville croit voir
   * toute sa tournée.
   *
   * ⚠️ « Restreint », pas « a une valeur » : le choix par défaut — « Tous les états »,
   * « Toutes les villes » — ne doit **pas** l'allumer, sinon tous les filtres sont allumés
   * en permanence et le repère ne dit plus rien. Ce paquet ne peut pas le deviner : quelle
   * valeur ne restreint rien est une affaire d'écran, d'où un booléen plutôt qu'une valeur
   * neutre déduite ici — c'est aussi ce que fait le prototype, qui compare à `'all'` dans
   * son contrôleur.
   *
   * Rien n'est ajouté à l'annonce : le libellé du déclencheur porte déjà la valeur
   * choisie, donc la restriction ne se joue pas sur la seule couleur.
   */
  active?: boolean
  disabled?: boolean
  required?: boolean
  /** Nom du champ pour une soumission de formulaire native. */
  name?: string
  /** Rattache le déclencheur à son libellé — `aria-labelledby` côté accessibilité. */
  'aria-labelledby'?: string
  /** Rattache le déclencheur à l'aide ou à l'avertissement de son `Field` (`hintId`). */
  'aria-describedby'?: string
  className?: string
}

export function Select<T extends string>({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = 'Sélectionner…',
  active = false,
  disabled,
  required,
  name,
  width,
  className,
  ...aria
}: SelectProps<T>) {
  return (
    <Base.Root
      items={options as SelectOption<T>[]}
      value={value}
      defaultValue={defaultValue}
      // La primitive passe aussi un second argument (détails de l'événement) : on ne le
      // propage pas, pour ne pas faire fuiter son API dans celle du design system.
      onValueChange={(next) => onValueChange?.(next)}
      disabled={disabled}
      required={required}
      name={name}
    >
      {/* L'état va au DOM, jamais à une classe : le module CSS le lit comme il lit ceux que
          la primitive publie. */}
      <Base.Trigger
        className={trigger({ width, className })}
        {...(active && { 'data-active': '' })}
        {...aria}
      >
        <Base.Value placeholder={placeholder} />
        <Base.Icon className={s.icon}>
          {/* `aria-hidden` : le chevron est décoratif, l'état ouvert/fermé est déjà
              annoncé par `aria-expanded` que porte la primitive. */}
          <ChevronDown size={16} aria-hidden />
        </Base.Icon>
      </Base.Trigger>

      <Base.Portal>
        <Base.Positioner sideOffset={4} alignItemWithTrigger={false}>
          <Base.Popup className={s.popup}>
            <Base.List>
              {options.map((option) => (
                <Base.Item
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className={s.item}
                >
                  <Base.ItemText>{option.label}</Base.ItemText>
                  <Base.ItemIndicator className={s.itemIndicator}>
                    <Check size={16} aria-hidden />
                  </Base.ItemIndicator>
                </Base.Item>
              ))}
            </Base.List>
          </Base.Popup>
        </Base.Positioner>
      </Base.Portal>
    </Base.Root>
  )
}
